import defaults from "lodash/defaults";
import find from "lodash/find";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import { DATE_FORMATS } from "../../../constants";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CalendarDuplicateWorkoutModal from "../../../parts/modals/calendar-duplicate-workout";
import CalendarMoveWorkoutModal from "../../../parts/modals/calendar-move-workout";
import CalendarShiftWorkoutModal from "../../../parts/modals/calendar-shift-workout";
import DuplicateWeekModal from "../../../parts/modals/duplicate-week";
import ImportWorkoutModal from "../../../parts/modals/import-workout";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import ProgressionModal from "../../../parts/modals/progression";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";
import VideoModal from "../../../parts/modals/video";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT, DATE_MONTH_DAY_WITHOUT_ZERO, UTC } =
  DATE_FORMATS;

import moment from "moment";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import {
  loadClientConfigurationAndFemaleCycleIfEnabled,
  loadClientConfigurationForAthlete,
} from "../../../utils/load-athlete-client-configuration";
import {
  getCoachFromAllAthletes,
  whoIsAthleteCoach,
} from "../../../utils/user-helpers";
import { getCalendarMonthGridBounds } from "../programs/program-insights/utils/inputs/calendarInsights";
import CalendarMonth from "./CalendarMonth";
import LogView from "./LogView";
import {
  expandLoggedFemaleCycleDays,
  getNormalizedLogs,
  getPredictedCycles,
} from "./menstrual-cycle/menstrual-cycle.helper";
import PlanView from "./PlanView";
import PreviewPhotoModal from "./progress-photos/components/PreviewPhotoModal";
import {
  findMealPhotosDayById,
  getPhotoThreadMapForDay,
} from "./utils/meal-photo-helpers";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const calcDateRange = (activeMonth, startingCalendarDay) => {
  const { startOfFirstWeek, endOfLastWeek } = getCalendarMonthGridBounds(
    activeMonth,
    startingCalendarDay
  );
  return { startOfFirstWeek, endOfLastWeek };
};

const shouldLoadAllAthletesFromOrganization = (locals) => {
  const isAllowToViewAllOrganizationsAthletes =
    isAdminOrOwner(locals.organizationRoleName) ||
    locals.organizationRoleName === "Team Coach";
  return Boolean(
    locals.activeUser.organizationId &&
      isAllowToViewAllOrganizationsAthletes &&
      !locals.allAthletesFromOrganization
  );
};

const maybeLoadAllAthletesFromOrganization = async (locals, load) => {
  if (shouldLoadAllAthletesFromOrganization(locals)) {
    await load("allAthletesFromOrganization");
  }
};

const buildCalendarApiQuery = (locals, query, startDate, endDate) => {
  const apiQuery = { startDate, endDate };
  if (query.athlete) apiQuery.proxyAthlete = query.athlete;

  const athleteIdForCoach = query.athlete || locals.athlete?._id;
  if (query.organizationCoach && athleteIdForCoach) {
    apiQuery.organizationCoachId = whoIsAthleteCoach(
      locals.activeUser.athletes,
      athleteIdForCoach,
      locals.activeUser._id,
      query.organizationCoach
    );
  } else if (
    athleteIdForCoach &&
    locals.activeUser?.organizationId &&
    locals.allAthletesFromOrganization?.length
  ) {
    const { organizationCoachId } = getCoachFromAllAthletes(
      locals.activeUser,
      athleteIdForCoach,
      locals.allAthletesFromOrganization
    );
    if (organizationCoachId) {
      apiQuery.organizationCoachId = organizationCoachId;
    }
  }

  if (locals.organizationOwner?._id) {
    apiQuery.organizationOwnerId = locals.organizationOwner._id;
  }

  return apiQuery;
};

const CalendarMonthController = async (
  { req, res, locals, flash, load, api },
  next
) => {
  if (req.query.view && req.query.view !== "month") {
    return next();
  }

  const { query = {} } = req;
  defaults(query, {
    month: moment().format(DATE_YEAR_FORMAT),
    view: "month",
    contentType: "training",
    editCalendar: false,
    metrics: false,
    exercise: "all",
    day: moment().format(DATE_DAY_FORMAT),
    error: false,
  });

  if (query.error) {
    flash("error", query.error);
    return res.refreshWith({ error: false });
  }

  locals.activeMonth = moment.utc(query.month, DATE_YEAR_FORMAT);

  const startingCalendarDay = locals.activeUser.startingCalendarDay || 0;
  const { startOfFirstWeek, endOfLastWeek } = calcDateRange(
    locals.activeMonth,
    startingCalendarDay
  );
  locals.startOfFirstWeek = startOfFirstWeek;
  locals.endOfLastWeek = endOfLastWeek;
  const isCurrentMonth = locals.activeMonth.isSame(moment(), "month");

  if (query.contentType === "plan") {
    await maybeLoadAllAthletesFromOrganization(locals, load);
    const startDate = locals.startOfFirstWeek.format("YYYY-MM-DD");
    const endDateStr = locals.endOfLastWeek.format("YYYY-MM-DD");
    const apiQuery = buildCalendarApiQuery(
      locals,
      query,
      startDate,
      endDateStr
    );

    const response = await api("calendar-plan", {
      method: "GET",
      query: apiQuery,
    });
    const planData = await response.json();

    locals.planExercises = groupBy(planData.exercises, (e) =>
      moment.utc(e.utcDate?.toString() || e.date).format(DATE_DAY_FORMAT)
    );
    locals.planNutritionPlans = planData.nutritionPlans || [];
    locals.planMealPlans = planData.mealPlans;
    locals.planHabits = planData.habits;
    locals.planAnnualPhases = planData.annualPhases;
    locals.planAnnualEvents = planData.annualEvents || [];
    locals.planTargetGoals = planData.targetGoals;

    // "Tasks assigned by coach" tracking cards read the client configuration
    // directly (sections.tracking.items) — not a server-synthesized shape.
    const planAthleteId = query.athlete || get(locals, "athlete._id");
    await loadClientConfigurationForAthlete(load, planAthleteId);
    locals.planTrackingItems =
      locals.clientConfiguration?.sections?.tracking?.items || {};
    const rawCycleLogs = planData.femaleCycleDays || [];
    const cycleSettings = planData.femaleCycleSettings || null;
    const normalizedLogs = getNormalizedLogs(rawCycleLogs, cycleSettings);
    const startMonth = locals.activeMonth.format("YYYY-MM");
    const predictedCycles = cycleSettings
      ? getPredictedCycles(normalizedLogs, cycleSettings, startMonth, 2)
      : [];

    const expandedCycleDays = expandLoggedFemaleCycleDays(
      normalizedLogs,
      DATE_DAY_FORMAT
    );
    for (const pred of predictedCycles) {
      const start = moment.utc(pred.startDate);
      const end = moment.utc(pred.endDate);
      const d = start.clone();
      let dayNum = 1;
      while (d.isSameOrBefore(end)) {
        const dateKey = d.format(DATE_DAY_FORMAT);
        if (!expandedCycleDays.some((e) => e.date === dateKey)) {
          expandedCycleDays.push({
            date: dateKey,
            dayNumber: dayNum,
            type: "predicted",
          });
        }
        d.add(1, "day");
        dayNum++;
      }
    }
    locals.planFemaleCycleDays = expandedCycleDays;
    locals.planCheckInDays = (planData.checkInDays || []).map((d) =>
      moment.utc(String(d), "YYYYMMDD").format(DATE_DAY_FORMAT)
    );

    const planCycleIds = uniqBy(
      (planData.exercises || []).filter((e) => e.cycle),
      (e) => String(e.cycle.id)
    ).map((exercise) => exercise.cycle.id);

    locals.cycles = planCycleIds.length
      ? await load("cycles", planCycleIds, query.athlete)
      : [];

    const planModal = get(query, "modal", {});
    locals.modal = {
      "activity-feed": <ActivityDrawer />,
    }[planModal.type];

    res.body = <PlanView />;
    return;
  }

  if (query.contentType === "log") {
    await maybeLoadAllAthletesFromOrganization(locals, load);
    const startDate = locals.startOfFirstWeek.format("YYYY-MM-DD");
    const endDateStr = locals.endOfLastWeek.format("YYYY-MM-DD");
    const apiQuery = buildCalendarApiQuery(
      locals,
      query,
      startDate,
      endDateStr
    );

    const toDateKey = (item) =>
      moment.utc(item.utcDate?.toString() || item.date).format(DATE_DAY_FORMAT);

    const response = await api("calendar-log", {
      method: "GET",
      query: apiQuery,
    });
    const logData = await response.json();

    locals.logExercises = groupBy(logData.exercises, toDateKey);
    locals.logFoodLogs = groupBy(logData.foodLogs, toDateKey);
    locals.logNutritionSummary = groupBy(logData.nutritionSummary, toDateKey);
    locals.logNutritionPlans = logData.nutritionPlans || [];
    locals.logHabitLogs = groupBy(logData.habitLogs, toDateKey);
    locals.logDailyStats = groupBy(logData.dailyStats, toDateKey);
    locals.logBodyMeasurements = groupBy(logData.bodyMeasurements, toDateKey);
    locals.logProgressPhotos = groupBy(logData.progressPhotos, toDateKey);
    locals.logMealPhotos = groupBy(logData.mealPhotos, toDateKey);

    const rawLogCycleLogs = logData.femaleCycleDays || [];
    const logCycleSettings = logData.femaleCycleSettings || null;
    const normalizedLogCycleLogs = getNormalizedLogs(
      rawLogCycleLogs,
      logCycleSettings
    );
    locals.logFemaleCycleDays = expandLoggedFemaleCycleDays(
      normalizedLogCycleLogs,
      DATE_DAY_FORMAT
    );

    await load("dayPersonalRecords", {
      start: locals.startOfFirstWeek.clone().startOf("day").format(UTC),
      end: isCurrentMonth
        ? moment().startOf("day").format(UTC)
        : locals.endOfLastWeek.clone().endOf("day").format(UTC),
      proxyAthlete: query.athlete,
      isCheckIn: true,
    });

    if (query.threadId) {
      await load("comments", {
        threadId: query.threadId,
      });
    } else {
      delete locals.comments;
    }

    if (query.dailyStatId) {
      const mealPhotosDay = findMealPhotosDayById(
        locals.logMealPhotos,
        query.dailyStatId
      );
      const photoThreadMap = mealPhotosDay
        ? getPhotoThreadMapForDay(mealPhotosDay)
        : [];
      if (photoThreadMap.length > 0) {
        await load("mealComments", { photoThreadMap });
      } else {
        delete locals.mealComments;
      }
    } else {
      delete locals.mealComments;
    }

    const logModal = get(query, "modal", {});
    locals.modal = {
      "preview-photo": (
        <PreviewPhotoModal
          dailyStatId={query.dailyStatId}
          photo={{
            type: query.photoType,
            pictureImg: query.pictureImg,
          }}
          threadId={query.threadId}
          locals={locals}
          utcDate={query.utcDate}
        />
      ),
      "exercise-set-video": (
        <JWPlayerVideoModal
          athleteId={query.uploaderId || query.athlete}
          exerciseToEdit={query.exerciseToEdit}
          locals={locals}
          mediaId={query.mediaId}
          movement={query.movement}
          originalId={query.originalId}
          primary={query.primary}
          reviewedByTheCoach={query.reviewedByTheCoach}
          secondary={query.secondary}
          setIndex={query.setIndex}
          type={query.type}
          uploaderId={query.uploaderId}
          videoIndex={query.videoIndex}
        />
      ),
      "activity-feed": <ActivityDrawer />,
    }[logModal.type];

    res.body = <LogView />;
    return;
  }

  const modal = get(query, "modal", {});

  // Cache remove for MSB-411

  const isWideRange =
    modal.type === "calendar-move-workout" ||
    modal.type === "calendar-duplicate-workout" ||
    modal.type === "calendar-shift-workout";

  const loadPromises = [
    isWideRange
      ? load("activeExercises", {
          start: moment(locals.startOfFirstWeek).subtract(2, "years"),
          end: moment(locals.endOfLastWeek).add(2, "years"),
          proxyAthlete: query.athlete,
          reqSource: "calendar",
        })
      : load("activeExercises", {
          start: locals.startOfFirstWeek,
          end: locals.endOfLastWeek,
          proxyAthlete: query.athlete,
          reqSource: "calendar",
        }),
    load("dayPersonalRecords", {
      start: locals.startOfFirstWeek.clone().startOf("day").format(UTC),
      end: isCurrentMonth
        ? moment().startOf("day").format(UTC)
        : locals.endOfLastWeek.clone().endOf("day").format(UTC),
      proxyAthlete: query.athlete,
      isCheckIn: true,
    }),
  ];

  loadPromises.push(
    load("activeSections", {
      start: locals.startOfFirstWeek,
      end: locals.endOfLastWeek,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    })
  );

  if (query.metrics) {
    loadPromises.push(
      load("calendarMetrics", {
        athlete: query.athlete,
        type: query.metrics,
        exercise: query.exercise,
      })
    );
  }

  if (shouldLoadAllAthletesFromOrganization(locals)) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  const athlete = query.athlete || get(locals, "athlete._id");

  loadPromises.push(load("athleteFlexibleProgram", { proxyAthlete: athlete }));

  if (modal.type === "calendar-add-exercise" && query.addType === "complex") {
    loadPromises.push(load("complexes"));
  }

  if (modal.type === "import-workout") {
    loadPromises.push(load("savedWorkouts"));
    loadPromises.push(load("workoutFolders"));
  }

  await Promise.all(loadPromises);

  if (!query.metrics) locals.calendarMetrics = null;

  // When using a wide-range activeExercises load, filter down to the visible month
  // window before deriving scheduledSessions and cycle ids to avoid "Select All"
  // selecting years of workout dates instead of the current month.
  const monthExercises = isWideRange
    ? locals.activeExercises.filter((e) => {
        const d = moment.utc(e.utcDate?.toString() || e.date);
        return (
          d.isSameOrAfter(locals.startOfFirstWeek, "day") &&
          d.isSameOrBefore(locals.endOfLastWeek, "day")
        );
      })
    : locals.activeExercises;

  locals.scheduledSessions = groupBy(monthExercises, (e) =>
    moment.utc(e.utcDate?.toString() || e.date).format(DATE_DAY_FORMAT)
  );

  locals.scheduledSections = groupBy(locals.activeSections || [], (s) =>
    moment.utc(s.utcDate?.toString() || s.date).format(DATE_DAY_FORMAT)
  );

  const cycles = uniqBy(
    monthExercises.filter((e) => e.cycle),
    "cycle.id"
  ).map((exercise) => exercise.cycle.id);
  await load("cycles", cycles, query.athlete);

  const duplicateDate = query.day ? moment(query.day) : startOfFirstWeek;

  const weeks = [];
  const startDays = [];
  const checkDuplicate = [];

  for (let i = 1; i <= 6; i++) {
    const start = duplicateDate.clone().add(i, "weeks");

    const end = duplicateDate.clone().add(i, "weeks").add(6, "days");

    const haveExercises = monthExercises.some(
      ({ date }) =>
        start.isSameOrBefore(moment(date)) && end.isSameOrAfter(moment(date))
    );

    checkDuplicate.push(haveExercises);
    startDays.push(start);
    weeks.push(
      `${start.format(DATE_MONTH_DAY_WITHOUT_ZERO)} - ${end.format(
        DATE_MONTH_DAY_WITHOUT_ZERO
      )}`
    );
  }

  const editExercise = query.editMode
    ? find(monthExercises, { _id: query.exercise })
    : {};

  if (athlete) {
    await loadClientConfigurationAndFemaleCycleIfEnabled({
      load,
      locals,
      proxyAthlete: athlete,
    });
  }

  locals.modal = {
    "calendar-add-exercise": (
      <CalendarAddExerciseModal
        editExercise={editExercise}
        reqSource="calendar"
      />
    ),
    "calendar-duplicate-workout": <CalendarDuplicateWorkoutModal />,
    "calendar-move-workout": <CalendarMoveWorkoutModal />,
    "calendar-shift-workout": <CalendarShiftWorkoutModal />,
    "calendar-duplicate-week": (
      <DuplicateWeekModal
        weeks={startDays}
        duplicateCheck={checkDuplicate}
        weeksOrder={weeks}
        from={query.day}
        calendarMode
        proxyAthlete={query.athlete}
      />
    ),
    progression: (
      <ProgressionModal
        from={query.day}
        calendarMode
        proxyAthlete={query.athlete}
      />
    ),
    video: <VideoModal />,
    "swap-exercise": <SwapExerciseModal exercise={editExercise} />,
    "import-workout": (
      <ImportWorkoutModal
        savedWorkouts={locals.savedWorkouts || []}
        workoutFolders={locals.workoutFolders || []}
        date={query.day}
        exercisesLength={query.exercisesLength}
        highestOrder={query.highestOrder}
        athlete={locals.athlete || locals.activeUserToRequest}
      />
    ),
    "activity-feed": <ActivityDrawer />,
  }[modal.type];

  res.body = (
    <CalendarMonth
      athleteProp={query.athlete}
      showBack={query.showBack || false}
    />
  );
};

export default CalendarMonthController;
